import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Tabs, Upload, Button, message, Switch, Space, Image, Typography, Row, Col, Card, Tooltip } from 'antd';
import { PlusOutlined, UploadOutlined, ArrowRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../../config';
import QuillEditor from '../common/QuillEditor';
import PushToEmModal from '../PushToEmModal';

const { Title: AntTitle, Text } = Typography;

interface RoomTypeEditProps {
  roomTypeId: number;
  hotelId: number;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  emHotelId?: number;
}

const LANGUAGES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'nl-NL', label: 'Dutch' },
  { code: 'de-DE', label: 'German' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'fr-FR', label: 'French' },
];

const RoomTypeEdit: React.FC<RoomTypeEditProps> = ({
  roomTypeId,
  hotelId,
  visible,
  onClose,
  onSuccess,
  emHotelId,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roomType, setRoomType] = useState<any>(null);
  const [images, setImages] = useState<any[]>([]);
  const [pushToEmVisible, setPushToEmVisible] = useState(false);
  const [hasEmContent, setHasEmContent] = useState(false);

  useEffect(() => {
    if (visible && roomTypeId) {
      loadRoomType();
    }
  }, [visible, roomTypeId]);

  const loadRoomType = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/room-types/${roomTypeId}`);
      const roomTypeData = response.data;
      setRoomType(roomTypeData);

      // Set form values
      form.setFieldsValue({
        isActive: roomTypeData.isActive,
        ...LANGUAGES.reduce((acc, lang) => {
          acc[`name_${lang.code}`] = roomTypeData.names?.[lang.code] || '';
          acc[`shortName_${lang.code}`] = roomTypeData.shortNames?.[lang.code] || '';
          acc[`description_${lang.code}`] = roomTypeData.descriptions?.[lang.code] || '';
          acc[`emName_${lang.code}`] = roomTypeData.emNames?.[lang.code] || '';
          acc[`emDescription_${lang.code}`] = roomTypeData.emDescriptions?.[lang.code] || '';
          return acc;
        }, {} as any),
      });

      // Load images after we have the room type data
      loadImages(roomTypeData.mewsId);
    } catch (error) {
      message.error('Failed to load room type');
    } finally {
      setLoading(false);
    }
  };

  const loadImages = async (mewsId: string) => {
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/images`);
      const roomTypeImages = response.data.filter((img: any) =>
        img.CategoryId === mewsId
      );
      setImages(roomTypeImages);
    } catch (error) {
      console.error('Failed to load images');
    }
  };

  const handleSave = async () => {
    try {
      console.log('Starting save...', { roomTypeId });
      const values = await form.validateFields();
      console.log('Form values:', values);
      setSaving(true);

      // Build names, shortNames, descriptions, and EM fields objects
      const names: Record<string, string> = {};
      const shortNames: Record<string, string> = {};
      const descriptions: Record<string, string> = {};
      const emNames: Record<string, string> = {};
      const emDescriptions: Record<string, string> = {};

      LANGUAGES.forEach((lang) => {
        if (values[`name_${lang.code}`]) {
          names[lang.code] = values[`name_${lang.code}`];
        }
        if (values[`shortName_${lang.code}`]) {
          shortNames[lang.code] = values[`shortName_${lang.code}`];
        }
        if (values[`description_${lang.code}`]) {
          descriptions[lang.code] = values[`description_${lang.code}`];
        }
        if (values[`emName_${lang.code}`]) {
          emNames[lang.code] = values[`emName_${lang.code}`];
        }
        if (values[`emDescription_${lang.code}`]) {
          emDescriptions[lang.code] = values[`emDescription_${lang.code}`];
        }
      });

      const payload = {
        names,
        shortNames,
        descriptions,
        emNames,
        emDescriptions,
        isActive: values.isActive,
      };
      console.log('Sending payload:', payload);

      const response = await axios.put(`${API_URL}/room-types/${roomTypeId}`, payload);
      console.log('Save response:', response);

      message.success('Room type updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Save error:', error);
      message.error(error.response?.data?.message || 'Failed to update room type');
    } finally {
      setSaving(false);
    }
  };

  const getSqliteData = () => {
    const values = form.getFieldsValue();
    const emNames: Record<string, string> = {};
    const emDescriptions: Record<string, string> = {};

    LANGUAGES.forEach(lang => {
      if (values[`emName_${lang.code}`]) {
        emNames[lang.code] = values[`emName_${lang.code}`];
      }
      if (values[`emDescription_${lang.code}`]) {
        emDescriptions[lang.code] = values[`emDescription_${lang.code}`];
      }
    });

    return { emNames, emDescriptions };
  };

  const handleCopyToEm = (langCode: string) => {
    const values = form.getFieldsValue();
    const name = values[`name_${langCode}`];
    const description = values[`description_${langCode}`];

    if (!name && !description) {
      message.warning('No original content to copy');
      return;
    }

    form.setFieldsValue({
      [`emName_${langCode}`]: name || '',
      [`emDescription_${langCode}`]: description || '',
    });

    // Trigger re-render to update button state
    setHasEmContent(true);
    message.success('Original content copied to EM fields');
  };

  const languageTabs = LANGUAGES.map((lang) => ({
    key: lang.code,
    label: lang.label,
    children: (
      <Row gutter={24} align="top">
        {/* Left side: Original Content */}
        <Col span={11}>
          <Card
            title="Original Content (From PMS)"
            size="small"
            headStyle={{ background: '#fafafa', fontWeight: 600 }}
            bodyStyle={{ padding: 16 }}
          >
            <Form.Item
              label="Name"
              name={`name_${lang.code}`}
            >
              <Input placeholder={`Room type name in ${lang.label}`} />
            </Form.Item>

            <Form.Item
              label="Short Name"
              name={`shortName_${lang.code}`}
            >
              <Input placeholder={`Short name in ${lang.label}`} />
            </Form.Item>

            <Form.Item
              label="Description"
              name={`description_${lang.code}`}
            >
              <QuillEditor placeholder={`Description in ${lang.label}`} height={150} />
            </Form.Item>
          </Card>
        </Col>

        {/* Middle: Copy button */}
        <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
          <Tooltip title="Copy original content to EM fields">
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowRightOutlined />}
              size="large"
              onClick={() => handleCopyToEm(lang.code)}
              style={{ boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)' }}
            />
          </Tooltip>
        </Col>

        {/* Right side: EM Content */}
        <Col span={11}>
          <Card
            title="EM Content"
            size="small"
            headStyle={{ background: '#f0f9ff', fontWeight: 600, color: '#1890ff' }}
            bodyStyle={{ padding: 16 }}
          >
            <Form.Item
              label="EM Name"
              name={`emName_${lang.code}`}
            >
              <Input placeholder={`EM name in ${lang.label}`} />
            </Form.Item>

            <Form.Item
              label="EM Description"
              name={`emDescription_${lang.code}`}
            >
              <QuillEditor placeholder={`EM description in ${lang.label}`} height={150} />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    ),
  }));

  return (
    <>
      {contextHolder}
      <Modal
        title="Edit Room Type"
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key="push"
            onClick={() => {
              // Check if already pushed
              if (roomType?.emCategoryId && roomType?.emPmsCategoryId) {
                messageApi.error('This room type has already been pushed to EM');
                return;
              }

              setPushToEmVisible(true);
            }}
            disabled={!getSqliteData() || Object.keys(getSqliteData().emNames || {}).length === 0 || (roomType?.emCategoryId && roomType?.emPmsCategoryId)}
          >
            {roomType?.emCategoryId && roomType?.emPmsCategoryId
              ? 'Already Pushed to EM'
              : 'Push to EM'}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>,
        ]}
        width={1200}
        destroyOnClose
        style={{ top: 20 }}
        styles={{
          body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
        }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
            <Text strong>Active:</Text>
            <Form.Item
              name="isActive"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch />
            </Form.Item>
            <Text type="secondary" style={{ fontSize: 13 }}>Enable or disable this room type</Text>
          </div>

          <Tabs items={languageTabs} />

          {images.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <AntTitle level={5}>Images ({images.length})</AntTitle>
              <Image.PreviewGroup>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                  {images.map((img) => (
                    <Image
                      key={img.ImageId}
                      src={img.Url}
                      alt={img.CategoryName}
                      width={150}
                      height={150}
                      style={{ objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            </div>
          )}
        </Form>
      </Modal>

      <PushToEmModal
        visible={pushToEmVisible}
        onClose={() => setPushToEmVisible(false)}
        entityType="room-type"
        entityId={roomTypeId}
        entityData={getSqliteData()}
        emHotelId={emHotelId}
        onSuccess={() => {
          message.success('Room type pushed to EM successfully');
          setPushToEmVisible(false);
        }}
      />
    </>
  );
};

export default RoomTypeEdit;
